/**
 * Curated catalogue of languages for the "add language" quick-pick.
 *
 * Each entry carries an English label, a native name (nice-to-have for the
 * picker), and a default gettext `pluralForms` expression. The plural rules are
 * the well-known CLDR-derived gettext expressions (see the GNU gettext manual
 * and the Translate Toolkit list); they are sensible defaults that a user can
 * still override before saving.
 *
 * This list intentionally lives on the frontend so adding a language never
 * blocks on a backend catalogue endpoint. If a server-served catalogue becomes
 * the better long-term design, this module can be swapped for a query without
 * touching the dialog. See COR-714.
 */

export interface PresetLanguage {
  /** BCP-47 / ISO-639 code, lowercase primary subtag. */
  code: string;
  /** English display label. */
  label: string;
  /** Endonym (native name); shown as a secondary hint in the picker. */
  native: string;
  /** Default gettext Plural-Forms expression. */
  pluralForms: string;
  /** Coarse grouping used only to order/section the picker. */
  region: 'european' | 'world';
}

// Shared gettext plural-forms expressions, grouped to avoid copy-paste drift.
const P_ONE = 'nplurals=1; plural=0;'; // East/Southeast Asian, Turkic-ish, no plural distinction
const P_TWO = 'nplurals=2; plural=(n != 1);'; // Germanic / most Romance / default Western
const P_TWO_GT1 = 'nplurals=2; plural=(n > 1);'; // French, Brazilian Portuguese, etc.
const P_RU =
  'nplurals=3; plural=(n%10==1 && n%100!=11 ? 0 : n%10>=2 && n%10<=4 && (n%100<12 || n%100>14) ? 1 : 2);';
const P_PL =
  'nplurals=3; plural=(n==1 ? 0 : n%10>=2 && n%10<=4 && (n%100<12 || n%100>14) ? 1 : 2);';
const P_CS = 'nplurals=3; plural=(n==1) ? 0 : (n>=2 && n<=4) ? 1 : 2;';
const P_SL =
  'nplurals=4; plural=(n%100==1 ? 0 : n%100==2 ? 1 : n%100==3 || n%100==4 ? 2 : 3);';
const P_RO =
  'nplurals=3; plural=(n==1 ? 0 : (n==0 || (n%100>0 && n%100<20)) ? 1 : 2);';
const P_LT =
  'nplurals=3; plural=(n%10==1 && n%100!=11 ? 0 : n%10>=2 && (n%100<10 || n%100>=20) ? 1 : 2);';
const P_LV = 'nplurals=3; plural=(n%10==1 && n%100!=11 ? 0 : n != 0 ? 1 : 2);';
const P_IS = 'nplurals=2; plural=(n%10!=1 || n%100==11);';
const P_GA = 'nplurals=5; plural=(n==1 ? 0 : n==2 ? 1 : n<7 ? 2 : n<11 ? 3 : 4);';
const P_CY = 'nplurals=4; plural=(n==1) ? 0 : (n==2) ? 1 : (n != 8 && n != 11) ? 2 : 3;';
const P_MT =
  'nplurals=4; plural=(n==1 ? 0 : n==0 || (n%100>1 && n%100<11) ? 1 : (n%100>10 && n%100<20) ? 2 : 3);';
const P_AR =
  'nplurals=6; plural=(n==0 ? 0 : n==1 ? 1 : n==2 ? 2 : n%100>=3 && n%100<=10 ? 3 : n%100>=11 ? 4 : 5);';

/**
 * Preset catalogue. Covers every European language plus the most common world
 * languages. Codes are unique and lowercase (region/script subtags preserve
 * their conventional casing, e.g. `pt-BR`, `zh-Hans`).
 */
export const PRESET_LANGUAGES: readonly PresetLanguage[] = [
  // ---- European ----
  { code: 'en', label: 'English', native: 'English', pluralForms: P_TWO, region: 'european' },
  { code: 'en-GB', label: 'English (UK)', native: 'English (UK)', pluralForms: P_TWO, region: 'european' },
  { code: 'en-US', label: 'English (US)', native: 'English (US)', pluralForms: P_TWO, region: 'european' },
  { code: 'de', label: 'German', native: 'Deutsch', pluralForms: P_TWO, region: 'european' },
  { code: 'fr', label: 'French', native: 'Français', pluralForms: P_TWO_GT1, region: 'european' },
  { code: 'es', label: 'Spanish', native: 'Español', pluralForms: P_TWO, region: 'european' },
  { code: 'es-419', label: 'Spanish (Latin America)', native: 'Español (Latinoamérica)', pluralForms: P_TWO, region: 'european' },
  { code: 'it', label: 'Italian', native: 'Italiano', pluralForms: P_TWO, region: 'european' },
  { code: 'pt', label: 'Portuguese', native: 'Português', pluralForms: P_TWO, region: 'european' },
  { code: 'pt-BR', label: 'Portuguese (Brazil)', native: 'Português (Brasil)', pluralForms: P_TWO_GT1, region: 'european' },
  { code: 'nl', label: 'Dutch', native: 'Nederlands', pluralForms: P_TWO, region: 'european' },
  { code: 'sv', label: 'Swedish', native: 'Svenska', pluralForms: P_TWO, region: 'european' },
  { code: 'da', label: 'Danish', native: 'Dansk', pluralForms: P_TWO, region: 'european' },
  { code: 'nb', label: 'Norwegian Bokmål', native: 'Norsk bokmål', pluralForms: P_TWO, region: 'european' },
  { code: 'nn', label: 'Norwegian Nynorsk', native: 'Norsk nynorsk', pluralForms: P_TWO, region: 'european' },
  { code: 'fi', label: 'Finnish', native: 'Suomi', pluralForms: P_TWO, region: 'european' },
  { code: 'is', label: 'Icelandic', native: 'Íslenska', pluralForms: P_IS, region: 'european' },
  { code: 'pl', label: 'Polish', native: 'Polski', pluralForms: P_PL, region: 'european' },
  { code: 'cs', label: 'Czech', native: 'Čeština', pluralForms: P_CS, region: 'european' },
  { code: 'sk', label: 'Slovak', native: 'Slovenčina', pluralForms: P_CS, region: 'european' },
  { code: 'sl', label: 'Slovenian', native: 'Slovenščina', pluralForms: P_SL, region: 'european' },
  { code: 'ru', label: 'Russian', native: 'Русский', pluralForms: P_RU, region: 'european' },
  { code: 'uk', label: 'Ukrainian', native: 'Українська', pluralForms: P_RU, region: 'european' },
  { code: 'be', label: 'Belarusian', native: 'Беларуская', pluralForms: P_RU, region: 'european' },
  { code: 'bg', label: 'Bulgarian', native: 'Български', pluralForms: P_TWO, region: 'european' },
  { code: 'sr', label: 'Serbian', native: 'Српски', pluralForms: P_RU, region: 'european' },
  { code: 'hr', label: 'Croatian', native: 'Hrvatski', pluralForms: P_RU, region: 'european' },
  { code: 'bs', label: 'Bosnian', native: 'Bosanski', pluralForms: P_RU, region: 'european' },
  { code: 'mk', label: 'Macedonian', native: 'Македонски', pluralForms: 'nplurals=2; plural=(n==1 || n%10==1 && n%100!=11) ? 0 : 1;', region: 'european' },
  { code: 'ro', label: 'Romanian', native: 'Română', pluralForms: P_RO, region: 'european' },
  { code: 'el', label: 'Greek', native: 'Ελληνικά', pluralForms: P_TWO, region: 'european' },
  { code: 'hu', label: 'Hungarian', native: 'Magyar', pluralForms: P_TWO, region: 'european' },
  { code: 'et', label: 'Estonian', native: 'Eesti', pluralForms: P_TWO, region: 'european' },
  { code: 'lv', label: 'Latvian', native: 'Latviešu', pluralForms: P_LV, region: 'european' },
  { code: 'lt', label: 'Lithuanian', native: 'Lietuvių', pluralForms: P_LT, region: 'european' },
  { code: 'tr', label: 'Turkish', native: 'Türkçe', pluralForms: P_TWO, region: 'european' },
  { code: 'ga', label: 'Irish', native: 'Gaeilge', pluralForms: P_GA, region: 'european' },
  { code: 'cy', label: 'Welsh', native: 'Cymraeg', pluralForms: P_CY, region: 'european' },
  { code: 'mt', label: 'Maltese', native: 'Malti', pluralForms: P_MT, region: 'european' },
  { code: 'sq', label: 'Albanian', native: 'Shqip', pluralForms: P_TWO, region: 'european' },
  { code: 'ca', label: 'Catalan', native: 'Català', pluralForms: P_TWO, region: 'european' },
  { code: 'eu', label: 'Basque', native: 'Euskara', pluralForms: P_TWO, region: 'european' },
  { code: 'gl', label: 'Galician', native: 'Galego', pluralForms: P_TWO, region: 'european' },
  { code: 'lb', label: 'Luxembourgish', native: 'Lëtzebuergesch', pluralForms: P_TWO, region: 'european' },
  { code: 'fo', label: 'Faroese', native: 'Føroyskt', pluralForms: P_TWO, region: 'european' },

  // ---- World ----
  { code: 'zh-Hans', label: 'Chinese (Simplified)', native: '简体中文', pluralForms: P_ONE, region: 'world' },
  { code: 'zh-Hant', label: 'Chinese (Traditional)', native: '繁體中文', pluralForms: P_ONE, region: 'world' },
  { code: 'ja', label: 'Japanese', native: '日本語', pluralForms: P_ONE, region: 'world' },
  { code: 'ko', label: 'Korean', native: '한국어', pluralForms: P_ONE, region: 'world' },
  { code: 'ar', label: 'Arabic', native: 'العربية', pluralForms: P_AR, region: 'world' },
  { code: 'he', label: 'Hebrew', native: 'עברית', pluralForms: P_TWO, region: 'world' },
  { code: 'hi', label: 'Hindi', native: 'हिन्दी', pluralForms: P_TWO, region: 'world' },
  { code: 'bn', label: 'Bengali', native: 'বাংলা', pluralForms: P_TWO, region: 'world' },
  { code: 'ur', label: 'Urdu', native: 'اردو', pluralForms: P_TWO, region: 'world' },
  { code: 'fa', label: 'Persian', native: 'فارسی', pluralForms: P_TWO_GT1, region: 'world' },
  { code: 'ta', label: 'Tamil', native: 'தமிழ்', pluralForms: P_TWO, region: 'world' },
  { code: 'te', label: 'Telugu', native: 'తెలుగు', pluralForms: P_TWO, region: 'world' },
  { code: 'th', label: 'Thai', native: 'ไทย', pluralForms: P_ONE, region: 'world' },
  { code: 'vi', label: 'Vietnamese', native: 'Tiếng Việt', pluralForms: P_ONE, region: 'world' },
  { code: 'id', label: 'Indonesian', native: 'Bahasa Indonesia', pluralForms: P_ONE, region: 'world' },
  { code: 'ms', label: 'Malay', native: 'Bahasa Melayu', pluralForms: P_ONE, region: 'world' },
  { code: 'fil', label: 'Filipino', native: 'Filipino', pluralForms: P_TWO_GT1, region: 'world' },
  { code: 'sw', label: 'Swahili', native: 'Kiswahili', pluralForms: P_TWO, region: 'world' },
  { code: 'af', label: 'Afrikaans', native: 'Afrikaans', pluralForms: P_TWO, region: 'world' },
  { code: 'az', label: 'Azerbaijani', native: 'Azərbaycan', pluralForms: P_TWO, region: 'world' },
  { code: 'ka', label: 'Georgian', native: 'ქართული', pluralForms: P_TWO, region: 'world' },
  { code: 'hy', label: 'Armenian', native: 'Հայերեն', pluralForms: P_TWO, region: 'world' },
  { code: 'kk', label: 'Kazakh', native: 'Қазақ', pluralForms: P_TWO, region: 'world' },
  { code: 'uz', label: 'Uzbek', native: 'Oʻzbek', pluralForms: P_TWO, region: 'world' },
];

/** Normalize a language code for case-insensitive comparison. */
export function normalizeCode(code: string): string {
  return code.trim().toLowerCase();
}

/**
 * Filter presets by a free-text query matching code, English label, or native
 * name. An empty query returns the full list (in catalogue order).
 */
export function searchPresetLanguages(query: string): readonly PresetLanguage[] {
  const q = query.trim().toLowerCase();
  if (!q) return PRESET_LANGUAGES;
  return PRESET_LANGUAGES.filter(
    (l) =>
      l.code.toLowerCase().includes(q) ||
      l.label.toLowerCase().includes(q) ||
      l.native.toLowerCase().includes(q),
  );
}
